import {
  EconoCashData,
  IAdministrationConfigData,
  IAgilePickup,
  IAgilePickupData,
  IAvailableConfig,
  IAvailableConfigData,
  ICashTodayConfig,
  ICashTodayConfigData,
  ICountry,
  ICountryConfig,
  ICountryConfigData,
  ICountryConfigResponse,
  ICountryData,
  ICurrency,
  IDocumentTypeData,
  IFilterConfig,
  IFilterConfigData,
  IForbiddenNextDayData,
  IOrderConfigCommon,
  IOrderConfigCommonData,
  IOrdersConfig,
  IOrdersConfigData,
  IPickupConfig,
  IPickupConfigData,
  IReportsConfig,
  IReportsConfigData,
  IReportTypeConfig,
  IReportTypeConfigData,
  ISearchEmployeeConfig,
  ISearchEmployeeConfigData,
  ISupplyConfig,
  ISupplyConfigData,
} from "@/entities";

export class Country implements ICountry {
  readonly id: number;
  readonly code?: string;
  readonly name?: string;
  readonly mainCurrency?: ICurrency;
  readonly showUsersGuide?: boolean;
  readonly maxServicesPoint?: number;
  readonly maxCodeSeal?: number;
  readonly documentTypes?: IDocumentTypeData[];

  constructor(data: ICountryData) {
    this.id = data.id;
    this.code = data.code;
    this.name = data.name;
    this.mainCurrency = data.mainCurrency;
    this.maxCodeSeal = data.maxCodeSeal;
    this.showUsersGuide = data.showUsersGuide || false;
    this.maxServicesPoint = data.maxServicesPoint ?? 0;
    this.documentTypes = data.documentTypes;
  }
}

export class CashTodayConfig implements ICashTodayConfig {
  readonly Enabled?: boolean;
  readonly ShowWarning: boolean;

  constructor(data: ICashTodayConfigData) {
    this.Enabled = data.Enabled || false;
    this.ShowWarning = data.ShowWarning || false;
  }
}

export class ReportsConfig implements IReportsConfig {
  readonly Enabled?: boolean;
  readonly ShowWarning: boolean;
  readonly ReportType?: ReportTypeConfig;

  constructor(data: IReportsConfigData) {
    this.Enabled = data.Enabled || false;
    this.ShowWarning = data.ShowWarning || false;
    this.ReportType = new ReportTypeConfig(data.ReportType || {});
  }
}

export class AdministrationConfig implements IAdministrationConfigData {
  readonly Enabled?: boolean;
  readonly ShowWarning: boolean;

  constructor(data: IAdministrationConfigData) {
    this.Enabled = data.Enabled || false;
    this.ShowWarning = data.ShowWarning || false;
  }
}

export class OrderConfigCommon implements IOrderConfigCommon {
  readonly AllowIntervalTime: boolean;
  readonly DefaultFinalTime?: number;
  readonly DefaultStartTime?: number;
  readonly MinimumRangeInHours?: number;
  readonly MinimumStartingIntervalInHours?: number;
  readonly SuggestedFinishingIntervalInHours?: number;
  readonly ForbiddenNextDay?: IForbiddenNextDayData;
  readonly EconoCash?: EconoCashData;

  constructor(data: IOrderConfigCommonData) {
    this.EconoCash = data.EconoCash;
    this.ForbiddenNextDay = data.ForbiddenNextDay;
    this.AllowIntervalTime = data.AllowIntervalTime || false;
    this.DefaultFinalTime = data.DefaultFinalTime;
    this.DefaultStartTime = data.DefaultStartTime;
    this.MinimumRangeInHours = data.MinimumRangeInHours;
    this.MinimumStartingIntervalInHours = data.MinimumStartingIntervalInHours;
    this.SuggestedFinishingIntervalInHours = data.SuggestedFinishingIntervalInHours;
  }
}

export class PickupConfig extends OrderConfigCommon implements IPickupConfig {
  readonly AgilePickup: AgilePickup;
  constructor(data: IPickupConfigData) {
    super(data);

    this.AgilePickup = new AgilePickup(data.AgilePickup || {});
  }
}

export class AgilePickup implements IAgilePickup {
  readonly Mode: string;
  readonly ShowPacks: boolean;

  constructor(data: IAgilePickupData) {
    this.Mode = data.Mode ?? "0";
    this.ShowPacks = data.ShowPacks ?? false;
  }
}

export class FilterConfig implements IFilterConfig {
  readonly NextDays: number;
  readonly PreviousDays: number;

  constructor(data: IFilterConfigData) {
    this.NextDays = data.NextDays;
    this.PreviousDays = data.PreviousDays;
  }
}

export class SupplyConfig implements ISupplyConfig {
  readonly MaximumQuantity?: number;

  constructor(data: ISupplyConfigData) {
    this.MaximumQuantity = data.MaximumQuantity;
  }
}

export class OrdersConfig implements IOrdersConfig {
  readonly Enabled: boolean;
  readonly Delivery: OrderConfigCommon;
  readonly Filter?: FilterConfig;
  readonly Pickup: PickupConfig;
  readonly Supply?: SupplyConfig;
  readonly TechnicalAssistance: OrderConfigCommon;
  readonly ShowWarning: boolean;

  constructor(data: IOrdersConfigData) {
    this.Enabled = data.Enabled || false;
    this.Delivery = new OrderConfigCommon(data.Delivery || {});
    this.Filter = data.Filter ? new FilterConfig(data.Filter) : undefined;
    this.Pickup = new PickupConfig(data.Pickup || {});
    this.Supply = data.Supply ? new SupplyConfig(data.Supply) : undefined;
    this.TechnicalAssistance = new OrderConfigCommon(data.TechnicalAssistance || {});
    this.ShowWarning = data.ShowWarning || false;
  }
}

export class SearchEmployeeConfig implements ISearchEmployeeConfig {
  readonly BarcodePostProcessorRegexPattern?: string;
  readonly BarcodeFormats?: string[];
  readonly Enabled?: boolean;
  readonly SearchWithBarcode?: boolean;
  readonly Type?: string;
  readonly DataProtectionLawConfig?: boolean;
  readonly ShowDataDocument?: boolean;
  readonly ApplyValidationDocument?: boolean;
  readonly ShowWarning: boolean;

  constructor(data: ISearchEmployeeConfigData) {
    this.BarcodePostProcessorRegexPattern = data.BarcodePostProcessorRegexPattern;
    this.BarcodeFormats = data.BarcodeFormats ?? [];
    this.Enabled = data.Enabled || false;
    this.SearchWithBarcode = data.SearchWithBarcode || false;
    this.Type = data.Type;
    this.DataProtectionLawConfig = data.DataProtectionLawConfig;
    this.ShowDataDocument = data.ShowDataDocument || false;
    this.ApplyValidationDocument = data.ApplyValidationDocument || false;
    this.ShowWarning = data.ShowWarning || false;
  }
}

export class CountryConfig implements ICountryConfig {
  readonly CashToday: CashTodayConfig;
  readonly Orders: OrdersConfig;
  readonly SearchEmployee: ISearchEmployeeConfig;
  readonly Reports: IReportsConfigData;
  readonly Administration: IAdministrationConfigData;

  constructor(data: ICountryConfigData) {
    this.CashToday = new CashTodayConfig(data.CashToday || {});
    this.Orders = new OrdersConfig(data.Orders || {});
    this.SearchEmployee = new SearchEmployeeConfig(data.SearchEmployee || {});
    this.Reports = new ReportsConfig(data.Reports || {});
    this.Administration = new AdministrationConfig(data.Administration || {});
  }
}

export class ReportTypeConfig implements IReportTypeConfig {
  readonly Client?: AvailableConfig;
  readonly Orders?: AvailableConfig;
  readonly SearchEmployee?: AvailableConfig;
  readonly Servicepoint?: AvailableConfig;
  readonly Subclient?: AvailableConfig;
  readonly Users?: AvailableConfig;

  constructor(data: IReportTypeConfigData) {
    this.Client = new AvailableConfig(data.Client || {});
    this.Orders = new AvailableConfig(data.Orders || {});
    this.SearchEmployee = new AvailableConfig(data.SearchEmployee || {});
    this.Servicepoint = new AvailableConfig(data.Servicepoint || {});
    this.Subclient = new AvailableConfig(data.Subclient || {});
    this.Users = new AvailableConfig(data.Users || {});
  }
}

export class AvailableConfig implements IAvailableConfig {
  readonly Enabled?: boolean;

  constructor(data: IAvailableConfigData) {
    this.Enabled = data.Enabled || false;
  }
}

export class CountryConfigResponse implements ICountryConfigResponse {
  enabledQueryFleetByPlate?: boolean;
  enabledQueryFleetByInternalNumber?: boolean;
  disabledExportEmployees?: boolean;
  enabledOrderFlowApproval?: boolean;
  enabledCancelOrders?: boolean;
  enabledSolServicesAPI?: boolean;
  enabledPlanningDate?: boolean;
  adjustmentMinutes?: number;
  enabledAssignServicePointByZone?: boolean;

  constructor(data: ICountryConfigResponse) {
    this.enabledQueryFleetByPlate = data.enabledQueryFleetByPlate;
    this.enabledQueryFleetByInternalNumber = data.enabledQueryFleetByInternalNumber;
    this.disabledExportEmployees = data.disabledExportEmployees;
    this.enabledOrderFlowApproval = data.enabledOrderFlowApproval;
    this.enabledCancelOrders = data.enabledCancelOrders;
    this.enabledSolServicesAPI = data.enabledSolServicesAPI;
    this.enabledPlanningDate = data.enabledPlanningDate;
    this.adjustmentMinutes = data.adjustmentMinutes;
    this.enabledAssignServicePointByZone = data.enabledAssignServicePointByZone;
  }
}
